
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { AuthHeader } from "@/components/auth/AuthHeader";
import { ResetPasswordForm } from "@/components/auth/ResetPasswordForm";
import { validatePassword } from "@/components/auth/PasswordValidation";

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [tokenChecked, setTokenChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Simply check if we have an active recovery session
    const verifySession = async () => {
      try {
        console.log("Reset Password - Verifying session...");
        
        // Get the current session
        const { data: { session } } = await supabase.auth.getSession();
        
        console.log("Reset Password - Session check:", session ? "Found session" : "No session found");
        
        // If we have an active session and it's a recovery session, we can proceed
        if (session) {
          setIsTokenValid(true);
        } else {
          // Try to process URL parameters/hash if present
          const hash = window.location.hash;
          const searchParams = new URLSearchParams(window.location.search);
          
          // Check if we have a recovery token in the URL
          if (hash && hash.includes('type=recovery')) {
            console.log("Reset Password - Found recovery hash");
            setIsTokenValid(true);
          } else if (searchParams.has('type') && searchParams.get('type') === 'recovery') {
            console.log("Reset Password - Found recovery parameter");
            setIsTokenValid(true);
          } else if (searchParams.has('code')) {
            console.log("Reset Password - Found code parameter");
            try {
              // Exchange code for session
              const { data, error } = await supabase.auth.exchangeCodeForSession(
                searchParams.get('code')!
              );
              
              if (error) throw error;
              if (data.session) {
                console.log("Reset Password - Successfully exchanged code for session");
                setIsTokenValid(true);
              }
            } catch (err) {
              console.error("Error exchanging code:", err);
              setError("Failed to process recovery code. Please request a new password reset link.");
            }
          } else {
            setError("No valid reset token found. Please request a new password reset link.");
          }
        }
      } catch (err) {
        console.error("Error in verification:", err);
        setError("Error verifying reset token. Please request a new reset link.");
      } finally {
        setTokenChecked(true);
      }
    };
    
    verifySession();
  }, []);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password
    const validationMessage = validatePassword(password);
    if (validationMessage) {
      toast.error(validationMessage);
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setIsLoading(true);

    try {
      console.log("Attempting to update password...");
      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        console.error("Error updating password:", error);
        
        // Handle specific error for same password
        if (error.message.includes("New password should be different from the old password")) {
          toast.error("New password must be different from your current password");
        } else {
          toast.error("Failed to reset password: " + error.message);
        }
        
        setIsLoading(false);
        return;
      }

      toast.success("Password has been reset successfully");
      
      // Slight delay before redirecting to login
      setTimeout(() => {
        navigate("/auth", { replace: true });
      }, 2000);
    } catch (error) {
      console.error("Error resetting password:", error);
      toast.error("Failed to reset password. Please try again or request a new reset link.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!tokenChecked) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-purple-100 via-white to-blue-100 p-4">
        <AuthHeader />
        
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Loading</CardTitle>
            <CardDescription>
              Verifying your reset password link...
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !isTokenValid) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-purple-100 via-white to-blue-100 p-4">
        <AuthHeader />
        
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invalid or Expired Link</CardTitle>
            <CardDescription>
              {error || "This password reset link appears to be invalid or has expired."}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground mb-4">
              Password reset links are only valid for a limited time. If your link has expired, you can request a new one.
            </p>
            <Button 
              onClick={() => navigate("/auth", { state: { requestPasswordReset: true } })}
              className="w-full mb-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            >
              Request New Reset Link
            </Button>
            <Button 
              onClick={() => navigate("/auth")}
              variant="outline"
              className="w-full"
            >
              Return to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-purple-100 via-white to-blue-100 p-4">
      <AuthHeader />
      
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Reset Your Password</CardTitle>
          <CardDescription>
            Enter and confirm your new password below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResetPasswordForm
            password={password}
            setPassword={setPassword}
            confirmPassword={confirmPassword}
            setConfirmPassword={setConfirmPassword}
            isLoading={isLoading}
            onSubmit={handleResetPassword}
          />
        </CardContent>
      </Card>
    </div>
  );
}
